/** Use as containerClassName to constrain height for pages with internal scrolling (e.g., DataTable) */
export const CONSTRAIN_HEIGHT_CLASS = "h-[calc(100dvh-var(--spacing-nav-top))] overflow-hidden";

/** Use as contentClassName for full-width pages (e.g., admin tables) */
export const FULL_WIDTH_CLASS = "xl:max-w-none";

/** Use as contentClassName with CONSTRAIN_HEIGHT_CLASS for full-bleed pages that lay out their own panes */
export const EDGE_TO_EDGE_CLASS = "min-h-0 p-0 md:p-0 xl:max-w-none";

export const STABLE_SCROLLBAR_CLASS = "scrollbar-gutter-stable";

import { Badge } from "@gigpro/ui/Badge";
import {
  Add,
  BookmarkOutline,
  Calendar,
  Coupon,
  Dashboard,
  Help,
  History,
  Home,
  Lock,
  Logout,
  Message,
  Money,
  Pros,
  QrCode,
  QwickAI,
  Settings,
  Store,
  Warning,
} from "@gigpro/ui/Icons";
import { env } from "config/env";
import { Briefcase, FileText, ListChecks, Link2, Radar, Rocket, ShieldAlert, User } from "lucide-react";
import { AskQwickPill } from "./AskQwickPill";
import type { NavItem, NavItemButton, NavItemLink } from "./types";

export const locations: NavItemLink = {
  type: "link",
  title: "Locations",
  icon: <Store />,
  matchSubpaths: [
    "/locations/add",
    "/locations/$locationId",
    "/locations/$locationId/info",
    "/locations/$locationId/teams",
    "/locations/$locationId/billing",
    "/locations/$locationId/add-ons",
    "/locations/$locationId/org-remap",
    "/organizations/$organizationId/$",
  ],
  path: "/locations",
};
export const marketplaceDashboards: NavItemLink = {
  type: "link",
  title: "Finance",
  icon: <Dashboard />,
  path: "/locations/$locationId/finance",
};
export const needHelp: NavItemLink = {
  type: "link",
  title: "Need help?",
  icon: <Help />,
  path: env.REACT_APP_CONTACT_SUPPORT_URL || "",
  external: true,
};
export const settings: NavItemLink = {
  type: "link",
  title: "Settings",
  icon: <Settings />,
  path: "/settings",
};
const getMessagesBase = (unreadMessageCount: number, path: string): NavItemLink => ({
  type: "link",
  title: "Messages",
  icon: (
    <Badge badgeContent={unreadMessageCount} max={100} invisible={unreadMessageCount === 0}>
      <Message className="size-3.5" />
    </Badge>
  ),
  path,
});

export const getMessages = (unreadMessageCount: number) =>
  getMessagesBase(unreadMessageCount, "/locations/$locationId/inbox");

export const getHireMessages = (unreadMessageCount: number) =>
  getMessagesBase(unreadMessageCount, "/locations/$locationId/hire/inbox");

export const logout: (doLogout: () => void) => NavItemButton = (doLogout) => ({
  type: "button",
  title: "Log out",
  icon: <Logout />,
  onClick: doLogout,
});

// The docked chat's entry, the "Ask Qwick" pill beside messages.
export const agentChat: (toggleChat: () => void) => NavItemButton = (toggleChat) => ({
  type: "button",
  title: "Ask Qwick",
  icon: (
    <span className="flex items-center gap-1.5 rounded-full border border-brand bg-black py-1 pr-3 pl-1.5 text-white transition-colors hover:bg-brand/20">
      <QwickAI className="size-4 shrink-0" />
      <span className="text-small font-semibold whitespace-nowrap">Ask Qwick</span>
    </span>
  ),
  onClick: toggleChat,
  variant: "pill",
});

// agent-web-onboarding: may open the onboarding takeover instead of the panel.
export const agentChatOnboarding: (onClick: () => void) => NavItemButton = (onClick) => ({
  type: "button",
  title: "Ask Qwick",
  icon: <AskQwickPill />,
  onClick,
  variant: "pill",
});

/// MARKETPLACE STUFF

export const marketplacePostGig: NavItemLink = {
  type: "link",
  title: "Post",
  icon: <Add />,
  path: "/locations/$locationId/gigs-add",
};
// Where R4 is on: Posting page V2 opens the chat beside it when the nav's Post brought the manager there.
export const marketplacePostGigFromNav: NavItemLink = { ...marketplacePostGig, search: { from_nav: true } };
export const marketplaceHome: NavItemLink = {
  type: "link",
  title: "Home",
  icon: <Home />,
  path: "/locations/$locationId/home",
};
export const marketplacePay: NavItemLink = {
  type: "link",
  title: "Pay",
  icon: <Money />,
  path: "/locations/$locationId/pay",
};
export const marketplaceSchedule: NavItemLink = {
  type: "link",
  title: "Schedule",
  icon: <Calendar />,
  path: "/locations/$locationId/schedule",
};
export const marketplaceHistory: NavItemLink = {
  type: "link",
  title: "History",
  icon: <History />,
  path: "/locations/$locationId/past-gigs",
};
export const marketplaceConnect: NavItemLink = {
  type: "link",
  title: "Connect",
  icon: <Pros />,
  path: "/locations/$locationId/connect",
};

export const powerUser: (toggleMenu: () => void) => NavItemButton = (toggleMenu) => ({
  type: "button",
  title: "Power Tools",
  icon: <Rocket />,
  onClick: toggleMenu,
});

export const adminTasks: NavItemLink = {
  type: "link",
  title: "Admin Tasks",
  icon: <ListChecks />,
  path: "/admin/task-ops",
};

export const adminInvoicing: NavItemLink = {
  type: "link",
  title: "Admin Invoicing",
  icon: <FileText />,
  path: "/admin/invoicing/runs",
};

export const adminItems: Array<NavItem> = [
  {
    type: "subheader",
    title: "Admin",
  },
  {
    type: "link",
    title: "Admin Locations",
    icon: <Store />,
    path: "/admin/locations",
  },
  {
    type: "link",
    title: "Admin Shifts",
    icon: <Calendar />,
    path: "/admin/gigs",
  },
  {
    type: "link",
    title: "Admin Jobs",
    icon: <Briefcase />,
    path: "/admin/jobs",
  },
  {
    type: "link",
    title: "Match Command Center",
    icon: <Radar />,
    path: "/admin/command-center",
  },
  {
    type: "link",
    title: "At-Risk Pros",
    icon: <ShieldAlert />,
    path: "/admin/at-risk-pros",
  },
  {
    type: "link",
    title: "Admin Alerts",
    icon: <Warning />,
    path: "/admin/admin-alerts",
  },
  {
    type: "link",
    title: "Admin Users",
    icon: <User />,
    path: "/admin/users",
  },
  {
    type: "link",
    title: "Admin Certifications",
    icon: <BookmarkOutline />,
    path: "/admin/certifications",
  },
  {
    type: "link",
    title: "Manage Pros",
    icon: <Pros />,
    path: "/admin/pros",
  },
  {
    type: "link",
    title: "Business Coupons",
    icon: <Coupon />,
    path: "/admin/business-coupons",
  },
  {
    type: "link",
    title: "Referral codes",
    icon: <QrCode />,
    path: "/admin/referral-code",
  },
  {
    type: "link",
    title: "Referral Promotions",
    icon: <Money />,
    path: "/admin/referral-promotions",
  },
  {
    type: "link",
    title: "Dynamic Links",
    icon: <Link2 />,
    path: "/admin/dynamic-links",
  },
];

/** Gated separately on the `manage_permissions` capability — the page redirects without it. */
export const adminPermissions: NavItem = {
  type: "link",
  title: "Admin Permissions",
  icon: <Lock />,
  path: "/admin/permissions",
};
